import { cityDuty, cityYards } from "../cityCommand/cityDuty";
import { scriptedCityTurn } from "../cityCommand/cityScript";
import { isCityCommand } from "../cityCommand/cityVerbs";
import { commandersAct } from "../court/commanders";
import { scriptedBuild, scriptedTurn } from "../court/courtScript";
import { answerMarks, callsAct } from "../court/general";
import { flee, moveKings, refillWill } from "../court/king";
import { mayorsAct } from "../court/mayors";
import { completeBuilds } from "../land/build";
import { produce } from "../land/economy";
import { captureByStanding } from "../land/territory";
import { settleDebts } from "../soldier/bounty";
import { discharge } from "../soldier/company";
import { prospect } from "../soldier/prospect";
import { scriptedSeats } from "../soldier/seatScript";
import { workPatch } from "../soldier/work";
import type { FactionId, RunState } from "../types/state";
import { fight } from "../units/combat";
import { breakContact, breakUnits } from "../units/morale";
import { moveUnits } from "../units/movement";
import { completeUnitBuilds } from "../units/roster";
import { fallOfCapitals } from "./capitals";
import { respawnSeats, scriptedFactions } from "./seats";
import { eliminated, landChangedSince, settle, trackDespair } from "./status";

/**
 * One tick of the run: the fixed order of the steps (ARCHITECTURE.md "Tick order") and the
 * scripts' turns. The order is the rule: a step reads what the steps before it wrote, so a new
 * step goes into one of the lists below at the place its rule needs, and the golden runs
 * (test/golden.test.ts) say whether any other run moved.
 */

/** A tick step: the state after it, or the same object when it has nothing to do this tick. */
export type TickStep = (state: RunState) => RunState;

/**
 * The land's steps, after the clock moves: production (supply, walls, healing), the soldier's
 * debts, the Kings' Will, capture by standing, a fleeing King, builds and units completing,
 * City Command's yards (on the production interval), the soldier seats' respawn, then prospecting.
 */
export const LAND_STEPS: readonly TickStep[] = [
  produce,
  settleDebts,
  refillWill,
  captureByStanding,
  flee,
  completeBuilds,
  completeUnitBuilds,
  cityYards,
  respawnSeats,
  prospect,
];

/**
 * The field's steps: the soldier's work route (CC5), moves on the move interval, the Kings'
 * walks and flight, the fall of capitals (K7), breaking contact, blows on the combat interval,
 * breaking, answered banners cleared, the soldier's discharge, then despair (the surrender count).
 */
export const FIELD_STEPS: readonly TickStep[] = [
  workPatch,
  moveUnits,
  moveKings,
  flee,
  fallOfCapitals,
  breakContact,
  fight,
  breakUnits,
  answerMarks,
  discharge,
  trackDespair,
];

/**
 * The delegates, after the scripts and on their own cadences: every mayor, both kingdoms
 * answering their Kings' calls (A33), every commander, and City Command's duty (on the tick
 * before each move step).
 */
export const DELEGATE_STEPS: readonly TickStep[] = [mayorsAct, callsAct, commandersAct, cityDuty];

function runSteps(state: RunState, steps: readonly TickStep[]): RunState {
  return steps.reduce((next, step) => step(next), state);
}

/**
 * Whether a scripted kingdom acts this tick on a cadence. The scripts are staggered (ADR-0012):
 * the i-th rival acts where tick % cadence is i % cadence, so none is always first and their
 * cost is spread over the cadence. The duel's one script is i = 0: every multiple, as before.
 */
export function isScriptTick(state: RunState, faction: FactionId, cadenceTicks: number): boolean {
  const seat = scriptedFactions(state).indexOf(faction);
  return seat >= 0 && state.tick % cadenceTicks === seat % cadenceTicks;
}

/**
 * Every scripted kingdom's turn, in the run's order (M1: with the human below the King, the
 * player's kingdom is scripted like the rivals'). A city-command kingdom plays City Command's
 * script at the script's place, one command each `scriptCommandTicks` (CC4), and its court
 * script does not run. Then the soldier seats no human holds, in seat order (K1).
 */
function scriptsAct(state: RunState): RunState {
  let next = state;
  for (const kingdom of scriptedFactions(next)) {
    const cities = next.rules.cityCommand;
    if (cities && isCityCommand(next, kingdom)) {
      if (isScriptTick(next, kingdom, cities.scriptCommandTicks) && !eliminated(next, kingdom)) {
        next = scriptedCityTurn(next, kingdom);
      }
      continue;
    }
    const plans = isScriptTick(next, kingdom, next.rules.aiCadenceTicks);
    const builds = isScriptTick(next, kingdom, next.rules.aiBuildCadenceTicks);
    if ((!plans && !builds) || eliminated(next, kingdom)) {
      continue;
    }
    if (plans) {
      next = scriptedTurn(next, kingdom);
    }
    if (builds) {
      next = scriptedBuild(next, kingdom);
    }
  }
  return scriptedSeats(next);
}

/**
 * One unit of game time: the clock, the land's steps, the field's steps, the verdict, then the
 * scripts and the delegates. The scripts act after the tick like a player acting between ticks,
 * so their orders wait for the next move step exactly as the player's do (the fairness mirror
 * measured 3/40 with the enemy acting before the move step; Increment 4 report).
 */
export function advanceTick(state: RunState): RunState {
  if (state.status !== "running") {
    return state;
  }
  const ownersBefore = state.hexes.map((hex) => hex.owner);
  const fought = runSteps(runSteps({ ...state, tick: state.tick + 1 }, LAND_STEPS), FIELD_STEPS);
  const settled = settle(fought, landChangedSince(fought, ownersBefore));
  if (settled.status !== "running") {
    return settled;
  }
  return runSteps(scriptsAct(settled), DELEGATE_STEPS);
}
